"use client"

import dynamic from "next/dynamic";
import {Clock, Loader2} from "lucide-react"
import {Button} from "@/components/ui/button"
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog"
import {VisuallyHidden} from "@radix-ui/react-visually-hidden";
import {useState} from "react";
// Locale-aware router so /home is prefixed for the current locale (/en/home for EN,
// /home for DE), consistent with the middleware root redirect.
import {useRouter} from "@/i18n/navigation";
import {AnonymousWarningModal} from "@/components/anonymous-warning-modal";
import {useTranslations} from "next-intl";

// code-splitting: Next.js will emit the sign-in form + Firebase bundle as a separate JS chunk.
const SigninForm = dynamic(
    () => import("@/app/[locale]/(auth)/signin/_components/signin-form")
        .then((mod) => mod.SigninForm),
    {
        loading: () => <Loader2 className="h-4 w-4 animate-spin" />,
    }
);

interface LoginButtonProps {
    title?: string;
    size?: "sm" | "lg" | "default" | null | undefined;
    variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
    children?: React.ReactNode;
    className?: string;
}

export function LoginButton({title, size, variant, children, className}:LoginButtonProps) {
    const t = useTranslations("auth.loginButton")
    const tForm = useTranslations("auth.form")
    const router = useRouter()
    const [open, setOpen] = useState(false)
    const [warningOpen, setWarningOpen] = useState(false)

    const handleSpäter = () => {
        setOpen(false)
        setWarningOpen(true)
    }

    const handleContinueAnonymous = async () => {
        setWarningOpen(false)
        const {signInWithAnon} = await import("@/app/[locale]/(auth)/signin/auth")
        try {
            await signInWithAnon()
        } catch {
            alert(tForm("anonSignInFailed"))
            return
        }
        router.refresh()
    }

    return (
        <>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                    <Button size={size} variant={variant} className={className}>
                        {children || title || t("defaultTitle")}
                    </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
                    <VisuallyHidden>
                        <DialogTitle>...</DialogTitle>
                    </VisuallyHidden>
                    {open && <SigninForm onClose={() => { setOpen(false); router.refresh(); }} onProviderSignInSuccess={() => router.push("/home")} />}
                    <Button
                        type="button"
                        variant="secondary"
                        className="w-full text-muted-foreground"
                        onClick={handleSpäter}
                    >
                        <Clock className="h-4 w-4"/>
                        {t("later")}
                    </Button>
                </DialogContent>
            </Dialog>

            <AnonymousWarningModal
                open={warningOpen}
                onOpenChange={setWarningOpen}
                onSignIn={() => { setWarningOpen(false); setOpen(true); }}
                onContinueAnonymous={handleContinueAnonymous}
            />
        </>
    )
}
